<template>
  <section class="gm" ref="gm">
    <div class="search"></div>
    <div class="map_wrapper" ref="wrapper">
      <span class="shadow"></span>
      <div class="map" ref="map" :style="{ height: `${mapHeight}px` }">
        <the-loader :isAbsolute="true"></the-loader>
      </div>
    </div>
    <div class="popup" v-show="showPopup">
      <div class="popup_content" v-html="popupContent"></div>
      <div class="buttons">
        <app-button @click="clearSelection" class="next is_white var_prev is_empty" ref="prevStep">
          <template v-slot:icon-prev>
            <svg class="prev" width="14" height="14" viewbox="0 0 14 14">
              <use xlink:href="#close"></use>
            </svg>
          </template>
          <template v-slot:icon-next>
            <svg class="next" width="14" height="14" viewbox="0 0 14 14">
              <use xlink:href="#close"></use>
            </svg>
          </template>
        </app-button>
        <app-button @click="selectCurrentStore" class="next is_red" ref="nextStep">
          {{ $t('choose') }}
        </app-button>
      </div>
    </div>
  </section>
</template>

<script>
/* global google */
import { mapGetters, mapState, mapActions } from 'vuex'
import TheLoader from './reservation/TheLoader.vue'
import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { gmStyle2 } from '@/assets/google.maps.style.js'
import AppButton from './atoms/AppButton.vue'
import date from 'date-and-time'

import gpsDistance from '../helpers/gpsDistance'

export default {
  data() {
    return {
      initMapPosition: {
        lat: 47.8095954,
        lng: 8.032245,
      },
      openedInfoView: null,
      map: null,
      mapHeight: 300,
      markers: [],
      userLocationMarker: null,
      showUserLocation: false,
      maxShopsToConsiderInMapZoom: 2,
      currentStore: {
        id: null,
        content: null,
      },
      distancesList: [],
      mapIcon: null,
      mapIconActive: null,
      showPopup: false,
      popupContent: '',
      openedPointId: null,
    }
  },
  components: {
    TheLoader,
    AppButton,
  },
  computed: {
    ...mapState('storages', {
      storages: 'all',
    }),
    ...mapState('user', { address: 'mapAddress', addressName: 'mapAddressName', shippingCountry: 'shippingCountry' }),
    ...mapGetters('storages', {
      availableStorageTypes: 'availableTypes',
    }),
    ...mapGetters('cart', ['isNextStepIsValid', 'isKioskAvailable', 'endDate']),
    ...mapState('cart', ['deliverPlaceID', 'pickUpStore', 'startDate']),
    mapPins() {
      return this.storages
        .filter((v) => v.pickup !== '' && v.pickup !== false)
        .filter((v) => this.isKioskAvailable || !v.isKiosk)
        .filter((v) => v.country === this.shippingCountry)
        .filter((v) => {
          const closed_from = v.closed_from ? date.parse(v.closed_from, 'DD.MM.YYYY').getTime() : false
          const close_to = v.closed_to ? date.parse(v.closed_to, 'DD.MM.YYYY').getTime() : false

          const close_next_from = v.closed_next_from ? date.parse(v.closed_next_from, 'DD.MM.YYYY').getTime() : false
          const close_next_to = v.closed_next_to ? date.parse(v.closed_next_to, 'DD.MM.YYYY').getTime() : false

          const startDate = this.startDate.getTime()
          const endDate = this.endDate.getTime()

          const firstCloseFrom = closed_from && close_to && startDate >= closed_from && startDate <= close_to // Start if between first close period
          const firstCloseTo = closed_from && close_to && endDate >= closed_from && endDate <= close_to // End if between first close period
          const secondCloseFrom =
            close_next_from && close_next_to && startDate >= close_next_from && startDate <= close_next_to // Start if between first close period
          const secondCloseTo =
            close_next_from && close_next_to && endDate >= close_next_from && endDate <= close_next_to // End if between first close period

          return !(firstCloseFrom || firstCloseTo || secondCloseFrom || secondCloseTo)
        })
        .map((v) => {
          return {
            id: v.id,
            lat: v.coordinates.lat,
            lng: v.coordinates.lng,
            name: v.name,
            type: v.storage,
            openHours: v.openHours,
            brand: v.brand,
            location: v.location,
            extraLocation: v.extraLocation,
            content: 'Popup Content',
            available: this.availableStorageTypes.indexOf(v.storage) > -1,
            isKiosk: v.isKiosk,
          }
        })
    },
    selectedStorePosition() {
      if (this.pickUpStore) {
        return {
          lat: this.pickUpStore.lat,
          lng: this.pickUpStore.lng,
        }
      } else return false
    },
  },
  created() {
    window.addEventListener('resize', this.calcMapHeight)
  },
  async mounted() {
    this.calcMapHeight()
    if (this.storages.length <= 0) {
      const from = date.format(this.startDate, 'YYYY-MM-DD')
      const to = date.format(this.endDate, 'YYYY-MM-DD')
      const $params = { from, to }
      await this.$store.dispatch('storages/getAll', $params)
    }
    this.loadGoogleMaps()
  },
  updated() {
    this.calcMapHeight()
  },
  destroyed() {
    window.removeEventListener('resize', this.calcMapHeight)
  },
  methods: {
    ...mapActions('cart', [
      'setPickUpPlaceType',
      'setPickUpPlace',
      'setOrderStepBySlug',
      'nextOrderStep',
      'prevOrderStep',
    ]),
    nextStep() {
      if (!this.isNextStepIsValid) {
        this.nextStepErrorUI()
      }
      // this.nextStepUI()
      this.nextOrderStep()
    },
    prevStep() {
      // this.prevStepUI()
      this.prevOrderStep()
    },
    loadGoogleMaps() {
      const loader = new Loader({
        apiKey: window.gm_object.key,
        version: 'weekly',
        libraries: ['places'],
      })

      loader.load().then(this.initGoogleMaps)
    },
    initGoogleMaps() {
      this.setMapIcons()
      const initPosition = this.selectedStorePosition || this.initMapPosition
      const zoom = this.selectedStorePosition ? 12 : 10

      this.map = new google.maps.Map(this.$refs.map, {
        center: initPosition,
        zoom,
        styles: gmStyle2,
        mapTypeControl: false,
        zoomControl: true,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
      })

      this.infoWindow = new google.maps.InfoWindow({
        content: '',
        disableAutoPan: true,
        shouldFocus: true,
      })

      this.infoWindow.addListener('closeclick', () => {
        this.changeOldActiveMarkerToInactiveColor()
      })

      google.maps.event.addListener(this.infoWindow, 'domready', () => {
        const button = document.querySelector('#store_button_select')
        const storeID = button.dataset.id

        button.addEventListener('click', () => {
          if (storeID != this.deliverPlaceID) {
            const point = this.mapPins.find((v) => v.id === storeID)
            this.selectStore(point)
          } else {
            // lets remove this selection
          }
        })
      })

      this.setMarkers()
      this.setUserLocationMarket()
      this.updateUserLocationMarker()
    },
    setMapIcons() {
      this.mapIcon = {
        path: 'M15,0 C23.2842712,0 30,6.74747126 30,15.0708989 C30,17.943914 29.1998626,20.6291638 27.8112665,22.9139689 C26.2053387,25.556373 22.4970764,31.4367438 16.6864794,40.5550815 L16.6864794,40.5550815 C16.5321396,40.8023095 16.3237137,41.010902 16.0780521,41.1677002 C15.1469737,41.7619786 13.9104277,41.4889483 13.3161493,40.55787 L13.3161493,40.55787 L12.9969925,40.0545369 C7.38168097,31.2307356 3.7848623,25.5269525 2.20653653,22.9431874 C0.806978577,20.6520704 0,17.9561701 0,15.0708989 C0,6.74747126 6.71572875,0 15,0 Z M15,8.4 C11.3549207,8.4 8.4,11.3549207 8.4,15 C8.4,18.6450793 11.3549207,21.6 15,21.6 C18.6450793,21.6 21.6,18.6450793 21.6,15 C21.6,11.3549207 18.6450793,8.4 15,8.4 Z',
        fillColor: '#000',
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 1,
        anchor: new google.maps.Point(15, 42),
      }

      this.mapIconActive = { ...this.mapIcon, fillColor: '#df3d3d' }
    },
    setMarkers() {
      var latlngbounds = new google.maps.LatLngBounds()

      this.markers = this.mapPins
        .filter((v) => v.available)
        .map((point) => {
          const position = {
            lat: point.lat,
            lng: point.lng,
          }
          const name = point.name
          const icon = point.id === this.deliverPlaceID ? this.mapIconActive : this.mapIcon

          const marker = new google.maps.Marker({
            position,
            icon,
            name,
            animation: google.maps.Animation.DROP,
          })

          marker.id = point.id
          marker.settings = point

          if (point.available) {
            marker.addListener('click', () => this.openInfoView(marker))
          }

          latlngbounds.extend(position)

          return marker
        })

      const renderer = {
        render: ({ count, position }) => {
          const svg = window.btoa(`<svg fill="#DE2C1D" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
						<circle cx="120" cy="120" opacity="1" r="110" />
					</svg>`)
          return new google.maps.Marker({
            // label: { text: String(count), color: 'white', fontSize: '10px' },
            // position,
            // // adjust zIndex to be above other markers
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            position,
            icon: {
              url: `data:image/svg+xml;base64,${svg}`,
              scaledSize: new google.maps.Size(45, 45),
            },
            label: {
              text: String(count),
              color: 'rgba(255,255,255,0.9)',
              fontSize: '14px',
              FontFace: 'tt_firs_neue_demibold',
            },
          })
        },
      }

      new MarkerClusterer({ markers: this.markers, map: this.map, renderer })

      if (this.markers.length && !this.selectedStorePosition) {
        this.map.fitBounds(latlngbounds, 80)
      }
    },
    changeOldActiveMarkerToInactiveColor() {
      const openedMarker = this.markers.find((v) => v.id === this.openedPointId)
      if (openedMarker) {
        openedMarker.setIcon(this.mapIcon)
      }
    },
    openInfoView(marker) {
      this.changeOldActiveMarkerToInactiveColor()

      const pointData = marker.settings
      this.openedPointId = pointData.id

      const hours = pointData?.openHours.reduce((acc, cur) => {
        acc += `<span class="hours_row"><span class="hours_label">${this.$t(cur.day)}</span><span>${
          cur.time
        }</span></span>`
        return acc
      }, '')

      let content = `
      			<p><span class="title">${pointData.brand}</span><br/>${pointData.location}</p>
      			<p class="hours_block">${hours}</p>`
      let button = ''
      if (pointData.id != this.deliverPlaceID) {
        button = `<button id="store_button_select" data-id="${pointData.id}" class="button_choose" type="button">
      					<svg class="prev" width="16" height="14" viewbox="0 0 16 14">
      						<use xlink:href="#arrow-long"></use>
      					</svg>
      					<span class="label">${this.$t('choose')}</span>
      					<svg class="next" width="16" height="14" viewbox="0 0 16 14">
      						<use xlink:href="#arrow-long"></use>
      					</svg>
      				</button>`
      } else {
        button = `<button id="store_button_select" data-id="${pointData.id}" class="button_choose" type="button">
      					<span>${this.$t('selected')}</span>
      				</button>`
      }

      if (window.innerWidth >= 768) {
        content += button
        this.showPopup = false
        this.infoWindow.setContent(content)
        this.infoWindow.open({
          map: this.map,
          anchor: marker,
        })
        this.map.panTo(this.infoWindow.getPosition())
        this.map.panBy(0, -100)
      } else {
        this.openPopup(content)
      }

      marker.setIcon(this.mapIconActive)
    },
    openPopup(content) {
      this.showPopup = true
      this.popupContent = content
    },
    selectCurrentStore() {
      const point = this.mapPins.find((v) => v.id === this.openedPointId)
      this.selectStore(point)
    },
    clearSelection() {
      this.showPopup = false
      this.changeOldActiveMarkerToInactiveColor()
    },
    setUserLocationMarket() {
      const position = this.initMapPosition
      const name = this.$t('User Location')

      this.userLocationMarker = new google.maps.Marker({
        position,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
        },
        name,
        animation: google.maps.Animation.DROP,
        map: this.map,
      })

      this.userLocationMarker.setVisible(false)
    },
    updateUserLocationMarker() {
      if (this.address) {
        const { location } = this.address.geometry
        this.userLocationMarker.setPosition(location)
        // this.userLocationMarker.setVisible(true)
        this.map.panTo(location)
        this.calculateDistance()
      }
    },
    selectStore(store) {
      this.currentStore.id = store.id
      this.setPickUpPlace(store)
      this.nextOrderStep()
    },
    clearCurrentStore() {
      this.currentStore = {
        id: null,
        content: null,
      }
    },
    clearMarkers() {
      this.markers.map((v) => {
        v.setMap(null)
      })
      this.markers = []
    },
    async calculateDistance() {
      const originLocation = this.address.geometry.location
      const originGps = { lat: originLocation.lat(), lng: originLocation.lng() }
      const destinations = this.mapPins.map((v) => {
        return {
          id: v.id,
          lat: v.lat,
          lng: v.lng,
        }
      })

      const distancesList = destinations.map((v) => {
        return {
          ...v,
          distance: gpsDistance(originGps, v),
        }
      })

      const sortedDistancesList = distancesList.sort(this.sortByProperty('distance'))
      this.distancesList = sortedDistancesList
      this.showClosestShops()
    },
    showClosestShops() {
      if (!this.distancesList.length) return true
      var latlngbounds = new google.maps.LatLngBounds()

      for (var i = 0; i < Math.min(this.maxShopsToConsiderInMapZoom, this.distancesList.length); i++) {
        const element = this.distancesList[i]
        const marker = this.markers.find((v) => v.id === element.id)
        //Extend each marker's position in LatLngBounds object
        latlngbounds.extend(marker.position)
      }

      const bouncePadding = this.distancesList[0].distance > 5000 ? 80 : 80

      latlngbounds.extend(this.userLocationMarker.position)

      this.map.setCenter(latlngbounds.getCenter())
      this.map.fitBounds(latlngbounds, bouncePadding)
    },
    sortByProperty(property) {
      return function (a, b) {
        if (a[property] > b[property]) return 1
        else if (a[property] < b[property]) return -1

        return 0
      }
    },
    calcMapHeight() {
      const sizes = this.$refs.wrapper.getBoundingClientRect()
      const bottomBarSize = 80

      this.mapHeight = Math.max(0.5 * window.innerHeight, window.innerHeight - sizes.y - bottomBarSize)
    },
  },
  watch: {
    address(newValue) {
      this.updateUserLocationMarker(newValue)
    },
    availableStorageTypes(newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        this.clearMarkers()
        this.setMarkers()
        this.clearCurrentStore()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.map {
  width: 100%;
  height: 50vh;
  position: relative;

  .wrapper.is_absolute {
    z-index: 4;

    &::v-deep .loader {
      margin-top: 0;
    }
  }
}

.map_wrapper {
  position: relative;
  margin-top: -100px;

  @include mq($from: tablet) {
    margin-top: -187px;
  }

  .shadow {
    position: absolute;
    width: 100%;
    height: 55px;
    top: 0;
    left: 0;
    background-image: linear-gradient(180deg, $colorBeige 10%, rgba($colorBeige, 0) 100%);
    z-index: 1;
    pointer-events: none;

    // background-image: linear-gradient(180deg, #F5F2E7 10%, rgba(245,242,231,0.00) 100%);
  }
}

.popup {
  @include fontStyleCopyRegular;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: $colorWhite;
  padding: 20px;
  z-index: 14;

  @include mq($from: tablet) {
    display: none;
  }

  .buttons {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    button {
      margin-left: 20px;
      margin-top: 35px;
    }
  }

  &_content {
    display: flex;
    justify-content: space-between;
    position: relative;

    &:after {
      position: absolute;

      content: '';
      display: block;
      width: 100%;
      left: 0px;
      top: -20px;
      height: 2px;
      background: $colorRed;
    }
  }

  &::v-deep .title {
    font-family: $fontFirsNeueDemiBold;
  }

  &::v-deep .hours_block {
    margin-top: 5px;
    font-size: 12px;
    min-width: 210px;
  }

  &::v-deep .hours_label {
    display: inline-block;
    min-width: 80px;
  }

  &::v-deep .hours_row {
    display: flex;
  }
}
</style>

<style lang="scss">
.gm-style {
  @include fontStyleCopyRegular;

  button {
    @include button();
    margin-top: 20px;
  }

  [role='dialog'] .gm-style-iw-d + button {
    top: 5px !important;
    right: 5px !important;
    width: 22px !important;
    height: 22px !important;
    opacity: 1;

    span {
      mask-image: url('data:image/svg+xml;charset=UTF-8,<svg width="22px" height="22px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M11,0 C17.0751322,0 22,4.92486775 22,11 C22,17.0751322 17.0751322,22 11,22 C4.92486775,22 0,17.0751322 0,11 C0,4.92486775 4.92486775,0 11,0 Z M14.75,6 L11,9.75 L7.25,6 L6,7.25 L9.75,11 L6,14.75 L7.25,16 L11,12.25 L14.75,16 L16,14.75 L12.25,11 L16,7.25 L14.75,6 Z"></path></svg>') !important;
      width: 22px !important;
      height: 22px !important;
      margin: 0 !important;
    }

    &:hover span {
      background-color: $colorRed;
    }
  }

  .gm-style-iw-c {
    border-radius: 5px;
    box-shadow: none;
    padding: 20px 25px;
  }

  .gm-style-iw {
    @include fontStyleCopyRegular;
    // border-radius: 5px;

    .title {
      font-family: $fontFirsNeueDemiBold;
    }

    &-t {
      &::after {
        display: none;
      }
    }

    &-d {
      padding: 0 20px 25px 0;
    }
  }

  .title {
    font-family: $fontFirsNeueDemiBold;
  }

  .hours_block {
    margin-top: 5px;
    font-size: 12px;
  }

  .hours_label {
    display: inline-block;
    min-width: 80px;
  }

  .hours_row {
    display: flex;
  }
}
</style>
